// frontend/src/components/games/WordChainGame.jsx
// Word Chain - Connect words by last letter

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const WordChainGame = ({ session, onLeave, onRefresh }) => {
  const [currentWord, setCurrentWord] = useState('');
  const [nextWord, setNextWord] = useState('');
  const [wordChain, setWordChain] = useState([]);
  const [usedWords, setUsedWords] = useState(new Set());
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [gameStarted, setGameStarted] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (gameStarted && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameStarted) {
      handleTimeout();
    }
  }, [timeLeft, gameStarted]);

  const handleStartGame = async () => {
    try {
      await api.post(`/funzone/session/${session.sessionCode}/start`);
      
      // Start with a random word
      const startWords = ['apple', 'elephant', 'tiger', 'rainbow', 'ocean', 'nature'];
      const randomStart = startWords[Math.floor(Math.random() * startWords.length)];
      
      setCurrentWord(randomStart);
      setWordChain([{ word: randomStart, player: 'System' }]);
      setUsedWords(new Set([randomStart.toLowerCase()]));
      setGameStarted(true);
      setTimeLeft(20);
      setMessage(`Game started! Next word must start with "${randomStart.charAt(randomStart.length - 1).toUpperCase()}"`);
      
      onRefresh();
    } catch (error) {
      console.error('Start error:', error);
      alert(error.response?.data?.message || 'Failed to start game');
    }
  };

  const handleSubmitWord = async (e) => {
    e.preventDefault();
    
    if (!nextWord.trim()) {
      setMessage('❌ Please enter a word');
      return;
    }
    
    const word = nextWord.trim().toLowerCase();
    const lastLetter = currentWord.charAt(currentWord.length - 1).toLowerCase();
    const firstLetter = word.charAt(0).toLowerCase();
    
    // Validate word
    if (usedWords.has(word)) {
      setMessage('❌ This word was already used!');
      return;
    }
    
    if (firstLetter !== lastLetter) {
      setMessage(`❌ Word must start with "${lastLetter.toUpperCase()}"!`);
      return;
    }
    
    if (word.length < 3) {
      setMessage('❌ Word must be at least 3 letters!');
      return;
    }
    
    // Valid word!
    const points = calculatePoints(word);
    
    try {
      await api.put(`/funzone/session/${session.sessionCode}/score`, {
        userId: session.players[currentPlayerIndex].user._id,
        points
      });
      
      setWordChain([...wordChain, { 
        word, 
        player: session.players[currentPlayerIndex].nickname || session.players[currentPlayerIndex].user?.name,
        points 
      }]);
      
      setUsedWords(new Set([...usedWords, word]));
      setCurrentWord(word);
      setNextWord('');
      setMessage(`✅ Great! +${points} points! Next word must start with "${word.charAt(word.length - 1).toUpperCase()}"`);
      
      // Next player
      setCurrentPlayerIndex((currentPlayerIndex + 1) % session.players.length);
      setTimeLeft(20);
      
    } catch (error) {
      console.error('Submit error:', error);
      setMessage('❌ Error submitting word');
    }
  };

  const calculatePoints = (word) => {
    let points = 10; // Base points
    
    if (word.length >= 8) {
      points += 20; // Bonus for long words
    } else if (word.length >= 6) {
      points += 10;
    }
    
    return points;
  };

  const handleTimeout = () => {
    setMessage('⏰ Time\'s up! Moving to next player...');
    
    // Next player
    setCurrentPlayerIndex((currentPlayerIndex + 1) % session.players.length);
    setTimeLeft(20);
  };

  const handleFinishGame = async () => {
    try {
      await api.post(`/funzone/session/${session.sessionCode}/finish`);
      alert('Game finished! Check the leaderboard.');
      onLeave();
    } catch (error) {
      console.error('Finish error:', error);
    }
  };

  const getCurrentPlayer = () => {
    return session.players[currentPlayerIndex];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-emerald-700 to-teal-600 text-white p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex justify-between items-center bg-gray-800 rounded-lg p-4">
          <div>
            <h1 className="text-3xl font-bold">🔗 Word Chain</h1>
            <p className="text-gray-400">Session: #{session.sessionCode}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500">{timeLeft}s</div>
              <div className="text-sm text-gray-400">Time Left</div>
            </div>
            <button
              onClick={onLeave}
              className="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700"
            >
              Leave Game
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Players Panel */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4">👥 Players</h3>
          
          <div className="space-y-3 mb-6">
            {session.players.map((player, idx) => (
              <div 
                key={idx} 
                className={`rounded-lg p-4 ${
                  idx === currentPlayerIndex && gameStarted
                    ? 'bg-green-600 ring-4 ring-green-300'
                    : 'bg-gray-700'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold">
                      {player.nickname || player.user?.name}
                      {idx === currentPlayerIndex && gameStarted && (
                        <span className="ml-2 text-xs">👈 YOUR TURN</span>
                      )}
                    </div>
                  </div>
                  <div className="text-yellow-500 font-bold text-lg">
                    {player.score}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {!gameStarted && session.status === 'waiting' && (
            <button
              onClick={handleStartGame}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-bold hover:bg-green-700"
            >
              Start Game
            </button>
          )}
          
          {gameStarted && (
            <button
              onClick={handleFinishGame}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-bold hover:bg-purple-700"
            >
              Finish Game
            </button>
          )}

          {/* Rules */}
          <div className="mt-6 bg-blue-900 rounded-lg p-4">
            <h4 className="font-bold mb-2">📋 Rules:</h4>
            <ul className="text-sm space-y-1 text-gray-300">
              <li>• Last letter = next first letter</li>
              <li>• No repeating words</li>
              <li>• Min 3 letters</li>
              <li>• Long words = bonus points</li>
              <li>• 20 seconds per turn</li>
            </ul>
          </div>
        </div>

        {/* Main Game Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Current Status */}
          {gameStarted && (
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">Current Word:</h3>
                <div className="text-6xl font-bold text-yellow-500 mb-4">
                  {currentWord.toUpperCase()}
                </div>
                <p className="text-2xl text-gray-300">
                  Next word must start with: 
                  <span className="text-green-400 font-bold ml-2">
                    {currentWord.charAt(currentWord.length - 1).toUpperCase()}
                  </span>
                </p>
              </div>

              {/* Message */}
              {message && (
                <div className={`text-center p-4 rounded-lg mb-4 ${
                  message.startsWith('✅') ? 'bg-green-600' :
                  message.startsWith('❌') ? 'bg-red-600' :
                  'bg-blue-600'
                }`}>
                  {message}
                </div>
              )}

              {/* Input Form */}
              <form onSubmit={handleSubmitWord} className="flex gap-3">
                <input
                  type="text"
                  value={nextWord}
                  onChange={(e) => setNextWord(e.target.value)}
                  placeholder={`Word starting with "${currentWord.charAt(currentWord.length - 1).toUpperCase()}"...`}
                  className="flex-1 px-6 py-4 bg-gray-700 rounded-lg text-white text-xl focus:outline-none focus:ring-4 focus:ring-green-500"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-green-600 rounded-lg hover:bg-green-700 font-bold text-xl"
                >
                  Submit
                </button>
              </form>

              {/* Scoring Guide */}
              <div className="mt-4 bg-gray-700 rounded-lg p-4">
                <h4 className="font-bold mb-2">💰 Scoring:</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>3-5 letters: <span className="text-yellow-500">+10 pts</span></div>
                  <div>6-7 letters: <span className="text-yellow-500">+20 pts</span></div>
                  <div>8+ letters: <span className="text-yellow-500">+30 pts</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Word Chain History */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold mb-4">📜 Word Chain History</h3>
            
            {wordChain.length === 0 ? (
              <p className="text-center text-gray-400 py-8">
                Game hasn't started yet...
              </p>
            ) : (
              <div className="space-y-2">
                {wordChain.map((item, idx) => (
                  <div key={idx} className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-green-400">
                        #{idx + 1}
                      </span>
                      <div>
                        <div className="text-xl font-bold">
                          {item.word.toUpperCase()}
                        </div>
                        <div className="text-sm text-gray-400">
                          by {item.player}
                        </div>
                      </div>
                    </div>
                    {item.points && (
                      <div className="text-yellow-500 font-bold text-lg">
                        +{item.points} pts
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Waiting Screen */}
          {!gameStarted && session.status === 'waiting' && (
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <h2 className="text-4xl font-bold mb-4">🔗 Word Chain Challenge!</h2>
              <p className="text-xl text-gray-400 mb-8">
                Chain words together using the last letter!
              </p>
              
              <div className="bg-gray-700 rounded-lg p-6 mb-6">
                <h3 className="text-2xl font-bold mb-4">Example:</h3>
                <div className="flex items-center justify-center gap-4 text-2xl">
                  <span className="font-bold">APPLE</span>
                  <span>→</span>
                  <span className="font-bold">ELEPHANT</span>
                  <span>→</span>
                  <span className="font-bold">TIGER</span>
                  <span>→</span>
                  <span className="font-bold">RAINBOW</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-green-900 rounded-lg p-4">
                  <div className="text-3xl mb-2">✓</div>
                  <div className="font-bold">Valid</div>
                  <div className="text-sm text-gray-400">First letter matches last letter</div>
                </div>
                <div className="bg-red-900 rounded-lg p-4">
                  <div className="text-3xl mb-2">✗</div>
                  <div className="font-bold">Invalid</div>
                  <div className="text-sm text-gray-400">No repeats, min 3 letters</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WordChainGame;
